// frontend/src/components/engineer/EngineerMainContent.tsx
// UPDATED WITH APPLY LEAVE, ANNUAL LEAVE, LEAVE REPORTS & DUTY RESUME ROUTES
// PHOENICIAN TECHNICAL SERVICES LLC

import React, { useMemo, useTransition, useCallback } from 'react';
import { AuthUser } from '../../services/auth.service';
import CreateOvertimePage from './pages/CreateOvertimePage';
import ReportsHistoryPage from './pages/ReportsHistoryPage';
import NoBreakPage from './pages/NoBreakPage';
import EditOvertimePage from './pages/EditOvertimePage';
import ViewTimesheetsPage from './pages/ViewTimesheetsPage';
import DashboardPage from './pages/DashboardPage';
import DashboardOvertimeWidget from './sections/DashboardOvertimeWidget';
import EngineerManpowerPage from './pages/EngineerManpowerPage';
import EngineerApplyLeavePage from './pages/EngineerApplyLeavePage';
import EngineerApplyAnnualLeavePage from './pages/EngineerApplyAnnualLeavePage';
import EngineerLeaveReportsPage from './pages/EngineerLeaveReportsPage';
import EngineerDutyResumePage from './pages/EngineerDutyResumePage'; // 🆕 NEW: Duty Resume Tracking
import styles from './EngineerMainContent.module.css';

export interface EngineerMainContentProps {
  activePage: string;
  projectId?: string;
  onNavigate?: (page: string, projectId?: string) => void;
  isTransitioning?: boolean;
  isSidebarCollapsed?: boolean;
  user?: AuthUser;
}

// Simple coming soon component
const ComingSoonPage: React.FC<{ title: string; onNavigate: (page: string) => void }> = ({ title, onNavigate }) => (
  <div className={styles.placeholderPage}>
    <h2>🚧 {title}</h2>
    <p>This feature is coming soon. We're working hard to bring you amazing engineering tools!</p>
    <button 
      onClick={() => onNavigate('dashboard')}
      className={styles.backToDashboard}
      style={{
        background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
        color: 'white',
        border: 'none',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
    >
      Back to Dashboard
    </button>
  </div>
);

const EngineerNightShiftPage = React.lazy(() => 
  import('./pages/EngineerNightShiftPage')
);

// Enhanced dashboard overview with professional UI
const SimpleDashboardOverview: React.FC<{ user?: AuthUser; onNavigate: (page: string, projectId?: string) => void }> = ({ user, onNavigate }) => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const quickActions = [
    {
      id: 'create-overtime',
      title: 'Create Overtime Request',
      description: 'Submit a new overtime request for approval',
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 6v6l4 2"/>
        </svg>
      ),
      color: '#c29e5d'
    },
    {
      id: 'no-break',
      title: 'No Break Management',
      description: 'Manage your break time and schedules',
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="8" r="3"/>
          <path d="M12 11v13m-7-5l7-2 7 2"/>
        </svg>
      ),
      color: '#c29e5d'
    },
    {
      id: 'reports-history',
      title: 'View Reports & History',
      description: 'Access your historical reports and records',
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9 11H3m6 0v6m0-6l-5 5M15 13h6m-6 0v-6m0 6l5-5"/>
          <rect x="3" y="3" width="18" height="18" rx="2"/>
        </svg>
      ),
      color: '#c29e5d'
    },
    {
      id: 'view-timesheets',
      title: 'View Timesheets',
      description: 'Review and manage your timesheet entries',
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      color: '#c29e5d'
    }
  ];

  return (
    <div className={styles.contentGrid}>
      {/* Left Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Welcome Section - Professional Header */}
        <div style={{
          background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
          color: 'white',
          padding: '2.5rem',
          borderRadius: '1rem',
          boxShadow: '0 10px 30px rgba(194, 158, 93, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
          }}/>
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h1 style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              margin: '0 0 0.5rem 0',
              letterSpacing: '-0.025em'
            }}>
              Welcome back, {user?.name || 'Engineer'}!
            </h1>
            <p style={{ 
              fontSize: '1.125rem', 
              opacity: '0.95', 
              margin: '0',
              fontWeight: '400'
            }}>
              {currentDate}
            </p>
          </div>
        </div>

        {/* Quick Actions Grid - Professional Card Design */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '1rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          border: '1px solid rgba(194, 158, 93, 0.1)',
        }}>
          <h2 style={{ 
            margin: '0 0 1.75rem 0', 
            fontSize: '1.375rem', 
            fontWeight: '600', 
            color: '#1f2937',
            letterSpacing: '-0.02em'
          }}>
            Quick Actions
          </h2>
          
          {user?.hasOvertimeAccess ? (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1rem'
            }}>
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => onNavigate(action.id)}
                  style={{
                    background: 'white',
                    border: '2px solid #f3f4f6',
                    borderRadius: '0.75rem',
                    padding: '1.25rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    textAlign: 'left',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = action.color;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 12px 24px ${action.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#f3f4f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Icon Container */}
                  <div 
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.5rem',
                      background: `${action.color}15`,
                      color: action.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {action.icon}
                  </div>
                  
                  {/* Text Content */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: '0 0 0.375rem 0',
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      letterSpacing: '-0.01em'
                    }}>
                      {action.title}
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      lineHeight: '1.5'
                    }}>
                      {action.description}
                    </p>
                  </div>
                  
                  {/* Arrow Icon */}
                  <div style={{
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              background: '#f9fafb',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ color: '#6b7280', margin: 0 }}>
                You don't have access to overtime features. Please contact your administrator.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Today's Overtime Widget */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {user?.hasOvertimeAccess && (
          <DashboardOvertimeWidget 
            user={user}
            onNavigate={onNavigate}
          />
        )}
      </div>
    </div>
  );
};

const EngineerMainContent: React.FC<EngineerMainContentProps> = React.memo(({ 
  activePage, 
  projectId,
  onNavigate,
  isTransitioning = false, 
  isSidebarCollapsed = false,
  user
}) => {
  console.log('EngineerMainContent rendering with activePage:', activePage, projectId ? `projectId: ${projectId}` : '');
  
  const [isPending, startTransition] = useTransition();

  const handleNavigate = useCallback((page: string, params?: string) => {
    if (onNavigate) {
      startTransition(() => {
        onNavigate(page, params);
      });
    }
  }, [onNavigate]);

  const renderPageContent = useMemo(() => {
    const pageKey = `${activePage}-${projectId || 'default'}`;
    
    switch (activePage) {
      case 'dashboard':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <DashboardPage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      case 'create-overtime':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <CreateOvertimePage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      case 'manpower':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EngineerManpowerPage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      // Apply Leave Page Route (for Sick, Emergency, Local Leave)
      case 'apply-leave':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EngineerApplyLeavePage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      // Apply Annual Leave Page Route (with Airport Search, Travel Details, Return Journey)
      case 'apply-annual-leave':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EngineerApplyAnnualLeavePage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      // Leave Reports Page Route
      case 'leave-reports':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EngineerLeaveReportsPage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      // 🆕 NEW: Duty Resume Tracking Page Route
      case 'duty-resume':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EngineerDutyResumePage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      case 'edit-overtime':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EditOvertimePage 
              requestId={projectId || ''}
              user={user}
              onNavigate={handleNavigate}
              onEditComplete={() => {
                handleNavigate('reports-history');
              }}
            />
          </div>
        );

      case 'night-shift-workers':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <EngineerNightShiftPage 
              onBack={() => handleNavigate('dashboard')}
              user={user}
            />
          </div>
        );

      case 'no-break':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <NoBreakPage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      case 'reports-history':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <ReportsHistoryPage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      case 'view-timesheets':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <ViewTimesheetsPage 
              user={user}
              onNavigate={handleNavigate}
            />
          </div>
        );

      case 'settings':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <ComingSoonPage 
              title="Settings" 
              onNavigate={handleNavigate}
            />
          </div>
        );

      default:
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <ComingSoonPage 
              title="Feature Under Development" 
              onNavigate={handleNavigate}
            />
          </div>
        );
    }
  }, [activePage, projectId, isTransitioning, handleNavigate, user]);

  return (
    <main className={`
      ${styles.engineerMainContent} 
      ${isPending ? styles.pending : ''} 
      ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}
    `}>
      {renderPageContent}
    </main>
  );
});

EngineerMainContent.displayName = 'EngineerMainContent';
export default EngineerMainContent;